import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Icon } from '../../../components/ui/Icon';
import { useEditorSelector, editorStore } from '../../editorStore';
import type { PanelCtx } from '../panelTypes';

function labelForSlot(index: number, kind: 'svg' | 'box'): string {
  return kind === 'svg' ? `Illustration ${index + 1}` : `Photo area ${index + 1}`;
}

export default function ImagesPanel({ ctx }: { ctx: PanelCtx }) {
  const assets = useEditorSelector((state) => state.assets);
  const [openPickerIndex, setOpenPickerIndex] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadTargetIndexRef = useRef<number | null>(null);
  const slotRefs = useRef<Array<HTMLDivElement | null>>([]);

  const slots = ctx.entry.images;

  const handleChooseAsset = useCallback(
    (index: number, assetId: string, justUploaded?: { url: string; alt?: string }) => {
      // A file that was only just uploaded is not in this closure's `assets` yet, so the caller hands it over.
      const asset = justUploaded ?? assets[assetId];
      if (!asset) return;
      const current = ctx.props.images?.[String(index)];
      const alt = current?.alt ?? asset.alt ?? '';
      ctx.update({
        images: {
          ...(ctx.props.images ?? {}),
          [String(index)]: { assetId, alt },
        },
      });
      const url = ctx.assetUrl(assetId) ?? asset.url;
      ctx.post({ type: 'setImage', id: index, url, alt });
      setOpenPickerIndex(null);
      setUploadError(null);
    },
    [assets, ctx],
  );

  const handleRemove = useCallback(
    (index: number) => {
      const newImages = { ...(ctx.props.images ?? {}) };
      newImages[String(index)] = undefined as never;
      ctx.update({ images: newImages });
      ctx.post({ type: 'setImage', id: index, url: null });
    },
    [ctx],
  );

  const handleAltChange = useCallback(
    (index: number, alt: string) => {
      if (alt.length > 200) return;
      const current = ctx.props.images?.[String(index)];
      if (!current) return;
      ctx.update({
        images: {
          ...(ctx.props.images ?? {}),
          [String(index)]: { ...current, alt },
        },
      });
      const asset = assets[current.assetId];
      if (asset) {
        const url = ctx.assetUrl(current.assetId) ?? asset.url;
        ctx.post({ type: 'setImage', id: index, url, alt });
      }
    },
    [assets, ctx],
  );

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const targetIndex = uploadTargetIndexRef.current;
      uploadTargetIndexRef.current = null;
      setUploadError(null);
      const uploaded = await editorStore.uploadAsset(file);
      if (uploaded) {
        handleChooseAsset(targetIndex ?? -1, uploaded.assetId, uploaded);
      } else {
        setUploadError('Could not upload this image. Please try a smaller file.');
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handleChooseAsset],
  );

  const handleUploadClick = (index: number) => {
    uploadTargetIndexRef.current = index;
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (!ctx.selection || ctx.selection.kind !== 'image') return;
    const index = Number(ctx.selection.id);
    if (Number.isInteger(index) && index >= 0 && index < slots.length) {
      setOpenPickerIndex(index);
      const slotEl = slotRefs.current[index];
      if (slotEl) {
        slotEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        slotEl.classList.add('dw-pulse');
        window.setTimeout(() => slotEl.classList.remove('dw-pulse'), 1400);
      }
    }
  }, [ctx.selection, slots.length]);

  if (slots.length === 0) {
    return (
      <div className="dw-panel">
        <div className="dw-panel-heading">
          <Icon name="image" size={18} />
          <h2>Images</h2>
        </div>
        <div className="dw-empty">
          This design has no replaceable images; its artwork is drawn in code.
        </div>
      </div>
    );
  }

  return (
    <div className="dw-panel">
      <div className="dw-panel-heading">
        <Icon name="image" size={18} />
        <h2>Images</h2>
      </div>
      <p className="dw-help-text">
        Replace the design&apos;s artwork with your own uploads. Use the Locate button to see a slot.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="dw-hidden-file"
        onChange={handleFileChange}
        aria-label="Upload image"
      />

      {uploadError ? (
        <div role="alert" className="dw-alert dw-alert--error">
          {uploadError}
        </div>
      ) : null}

      <div className="dw-image-slot-list">
        {slots.map((slot, index) => {
          const current = ctx.props.images?.[String(index)];
          const replaced = !!current && !!current.assetId;
          const thumbnail = current ? ctx.assetUrl(current.assetId) : null;
          return (
            <div
              key={`${slot.kind}-${index}`}
              className={`dw-image-slot ${openPickerIndex === index ? 'dw-image-slot--open' : ''}`}
              ref={(el) => {
                slotRefs.current[index] = el;
              }}
            >
              <div className="dw-image-slot-header">
                <div className="dw-thumbnail">
                  {thumbnail ? (
                    <img src={thumbnail} alt={current?.alt ?? slot.label ?? labelForSlot(index, slot.kind)} />
                  ) : (
                    <span className="dw-thumbnail-placeholder">
                      <Icon name="image" size={20} />
                    </span>
                  )}
                </div>
                <div className="dw-image-slot-info">
                  <h3>{slot.label ?? labelForSlot(index, slot.kind)}</h3>
                  <p>{replaced ? current?.alt || 'Custom image' : 'Original design artwork'}</p>
                </div>
                <div className="dw-image-slot-actions">
                  <button
                    type="button"
                    className="dw-button dw-button--ghost"
                    onClick={() =>
                      ctx.post({ type: 'focus', selector: `[data-img="${index}"]` })
                    }
                  >
                    <Icon name="eye" size={16} />
                    <span>Locate</span>
                  </button>
                  <button
                    type="button"
                    className="dw-button dw-button--ghost"
                    onClick={() => setOpenPickerIndex((prev) => (prev === index ? null : index))}
                  >
                    <Icon name={replaced ? 'edit' : 'plus'} size={16} />
                    <span>Replace</span>
                  </button>
                  {replaced ? (
                    <button
                      type="button"
                      className="dw-button dw-button--ghost dw-button--danger"
                      onClick={() => handleRemove(index)}
                    >
                      <Icon name="trash" size={16} />
                      <span>Remove</span>
                    </button>
                  ) : null}
                </div>
              </div>

              {replaced ? (
                <div className="dw-field">
                  <label className="dw-label" htmlFor={`dw-alt-${index}`}>
                    Alt text
                  </label>
                  <input
                    id={`dw-alt-${index}`}
                    className="dw-input"
                    type="text"
                    maxLength={200}
                    value={current?.alt ?? ''}
                    onChange={(e) => handleAltChange(index, e.target.value)}
                  />
                </div>
              ) : null}

              {openPickerIndex === index ? (
                <div className="dw-picker">
                  <div className="dw-picker-actions">
                    <button
                      type="button"
                      className="dw-button dw-button--primary"
                      onClick={() => handleUploadClick(index)}
                    >
                      <Icon name="upload" size={16} />
                      <span>Upload image</span>
                    </button>
                  </div>
                  {Object.keys(assets).length > 0 ? (
                    <div className="dw-asset-grid">
                      {Object.values(assets).map((asset) => (
                        <button
                          key={asset.assetId}
                          type="button"
                          className="dw-asset-tile"
                          onClick={() => handleChooseAsset(index, asset.assetId)}
                          aria-label={`Use ${asset.assetId}`}
                        >
                          <img src={asset.url} alt={asset.alt || asset.assetId} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="dw-muted">No uploaded images yet. Upload one above.</p>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
